import { useState, type ReactElement } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button.js';
import { useCatalog, useIosAppStoreUrl } from '@/lib/i18n/catalog-context.js';
import { readIosProbe, readIosStorage, rememberDismiss, shouldOfferIosApp } from '@/lib/ios-app.js';

/**
 * Предложение установить приложение для iPhone.
 *
 * ⚠️ Дублирует нативный Smart App Banner СОЗНАТЕЛЬНО. Тег `apple-itunes-app`
 * (server/lib/http/seo/seo.meta.ts) показывает плашку только в Safari; Chrome, Firefox и
 * Yandex на айфоне её не рисуют вовсе, и без этой строки их пользователи о приложении
 * не узнают ниоткуда. Пересечение в Safari — цена, которую платим осознанно: обе плашки
 * закрываются, и обе ведут в одно место.
 *
 * ⚠️ Строка В ПОТОКЕ над содержимым, а не плавающая плашка. Фиксированная перекрывала бы
 * шапку с меню и панель поиска задачи — то есть ровно то, ради чего на сайт заходят, — и
 * на каждый такой экран пришлось бы заводить исключение. В потоке она не может закрыть
 * ничего, а диалоги продукта живут слоями выше (z-1050/1055, components/ui/dialog.tsx).
 *
 * Условия показа и их обоснования — web/src/lib/ios-app.ts.
 */
export function IosAppBanner(): ReactElement | null {
  const c = useCatalog();
  const storeUrl = useIosAppStoreUrl();

  /**
   * Решение принимается ОДИН раз, в ленивом инициализаторе: строка агента, режим
   * standalone и отметка отказа в пределах вкладки не меняются, а пересчёт на каждый
   * рендер означал бы чтение localStorage на каждую навигацию по SPA.
   */
  const [open, setOpen] = useState(() =>
    shouldOfferIosApp({
      storeUrl,
      probe: readIosProbe(),
      storage: readIosStorage(),
      now: Date.now(),
    }),
  );

  if (!open) return null;

  const dismiss = (): void => {
    setOpen(false);
    rememberDismiss(readIosStorage(), Date.now());
  };

  return (
    <aside
      data-banner="ios-app"
      role="region"
      aria-label={c.common.iosApp.region}
      className="flex items-center gap-2 border-b border-mm-rule-warm bg-white px-2.5 py-2"
    >
      {/* Иконка приложения — та же, что браузер кладёт на экран «Домой». */}
      <img
        src="/apple-touch-icon.png"
        alt=""
        width={36}
        height={36}
        className="size-9 shrink-0 rounded-[9px]"
      />

      {/*
        min-w-0 обязателен: без него длинная строка растягивает флекс-контейнер и уводит
        кнопку за правый край экрана.
      */}
      <div className="min-w-0 flex-1">
        {/*
          Обе строки обрезаются многоточием, а не переносятся: на 375px рядом стоят иконка,
          кнопка и крестик, и при переносе плашка вырастает на пять строк, вытесняя
          содержимое страницы за первый экран.
        */}
        <p className="truncate text-meta font-bold text-mm-ink">{c.common.iosApp.title}</p>
        <p className="truncate text-caption text-mm-text-subtle">{c.common.iosApp.text}</p>
      </div>

      <Button asChild size="sm" className="shrink-0">
        {/*
          ⚠️ target="_blank" здесь уместен, в отличие от перехода на платёжный шлюз:
          человек уходит в стороннее приложение App Store, и возвращаться ему нужно на
          ту же страницу сайта, а не на её историю.
        */}
        <a href={storeUrl} target="_blank" rel="noopener noreferrer">
          {c.common.iosApp.install}
        </a>
      </Button>

      {/*
        Цель нажатия 40x40, хотя сам крестик мельче: промах по крестику на телефоне
        означает не «не попал», а уход в App Store.
      */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={c.common.iosApp.dismiss}
        onClick={dismiss}
        className="size-10 shrink-0 text-mm-text-subtle"
      >
        <X aria-hidden="true" />
      </Button>
    </aside>
  );
}
